/**
 * Collects every "Save Changes" blocker for the CPT-codes toolbar into a list
 * of human-readable messages.
 *
 * The toolbar already computes each blocker boolean; this hook only maps them
 * to copy so the disabled state and the button tooltip always agree.
 *
 * @module useSaveChangesValidationMessages
 */
import { useMemo } from "react";
import { SaveChangesValidationMessages } from "../../../../../constants/saveChangesValidationMessages";

interface UseSaveChangesValidationMessagesParams {
  /** Whether the CPT table has any unsaved edits. */
  hasChanges: boolean;
  /** Whether a save request is already in flight. */
  isSaving: boolean;
  /** Whether any CPT row has an invalid value on a save-blocking field. */
  hasCptTableAnyError: boolean;
  /** Whether any CPT value has drifted from the auth-letter values. */
  hasAuthLetterFieldMismatch: boolean;
}

export interface SaveChangesValidationMessagesResult {
  /** One message per failing validation, in stable display order. */
  validationMessages: string[];
  /** True when the Save Changes button should be disabled. */
  isSaveDisabled: boolean;
}

/**
 * Maps the CPT toolbar's save blockers to tooltip messages.
 *
 * @returns The failing validations as messages plus the combined disabled flag.
 */
export const useSaveChangesValidationMessages = ({
  hasChanges,
  isSaving,
  hasCptTableAnyError,
  hasAuthLetterFieldMismatch,
}: UseSaveChangesValidationMessagesParams): SaveChangesValidationMessagesResult => {
  const validationMessages = useMemo(() => {
    const messages: string[] = [];
    if (!hasChanges) {
      messages.push(SaveChangesValidationMessages.NO_CHANGES_TO_SAVE);
    }
    if (isSaving) {
      messages.push(SaveChangesValidationMessages.SAVE_IN_PROGRESS);
    }
    if (hasCptTableAnyError) {
      messages.push(SaveChangesValidationMessages.CPT_TABLE_HAS_ERRORS);
    }
    if (hasAuthLetterFieldMismatch) {
      messages.push(SaveChangesValidationMessages.AUTH_LETTER_FIELD_MISMATCH);
    }
    return messages;
  }, [hasChanges, isSaving, hasCptTableAnyError, hasAuthLetterFieldMismatch]);

  return {
    validationMessages,
    isSaveDisabled: validationMessages.length > 0,
  };
};
